
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getProductStock } from "../utils/cartHelper";
import ProductCartButton from "./ProductCartButton";

const API_BASE =
  "https://api.vanyacollectionsrajahmundry.com";

const VanyaBestSellers = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchBestSellers();

    const handleSync = () => fetchBestSellers();
    window.addEventListener("focus", handleSync);
    window.addEventListener("storage", handleSync);
    window.addEventListener("vanya_category_sync", handleSync);
    return () => {
      window.removeEventListener("focus", handleSync);
      window.removeEventListener("storage", handleSync);
      window.removeEventListener("vanya_category_sync", handleSync);
    };
  }, []);

  // ============================================================
  // FETCH BEST SELLERS
  // ============================================================

  const fetchBestSellers = async () => {
    try {
      const [res, catRes] = await Promise.all([
        fetch(`${API_BASE}/products/all`),
        fetch(`${API_BASE}/categories`).catch(() => null),
      ]);

      if (!res.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await res.json();
      const catData = catRes && catRes.ok ? await catRes.json() : [];
      const apiCategories = Array.isArray(catData) ? catData : [];
      const normalizeCat = (v = "") =>
        String(v || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

      const bestSellers = Array.isArray(data)
        ? data
            .filter(
              (p) =>
                !p.is_hold &&
                String(p.type || "")
                  .trim()
                  .toLowerCase() === "best seller"
            )
            .map((p) => {
              const normCat = normalizeCat(p.category);
              const matchedCat = apiCategories.find(
                (c) => normalizeCat(c?.category) === normCat
              );
              return matchedCat?.category
                ? { ...p, category: matchedCat.category }
                : p;
            })
        : [];

      setProducts(bestSellers);
    } catch (err) {
      console.error(
        "Error fetching best sellers:",
        err
      );
    }
  };

  // ============================================================
  // QUICK VIEW
  // ============================================================

  const handleQuickView = (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(`/product/${product.id}`);
  };

  return (
    <div style={styles.container}>
      {/* ====================================================== */}
      {/* SUCCESS / ERROR MESSAGE */}
      {/* ====================================================== */}

      {message && (
        <div
          className="cart-message"
          style={styles.cartMessage}
        >
          {message}
        </div>
      )}

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div style={styles.header}>
        <div>
          <span style={styles.tagline}>
            ✦ FAN FAVOURITES ✦
          </span>

          <h2 style={styles.title}>
            Best Sellers
          </h2>

          <div style={styles.underline}></div>
        </div>

        <Link
          to="/best-sellers"
          style={styles.viewAll}
          className="view-all-link"
        >
          View All →
        </Link>
      </div>

      {/* ====================================================== */}
      {/* PRODUCT GRID */}
      {/* ====================================================== */}

      <div
        style={styles.grid}
        className="bestsellers-grid"
      >
        {products.map((product) => {
          const isOutOfStock = getProductStock(product) <= 0;
          return (
          <div
            key={product.id}
            className="product-card-wrapper"
            style={styles.cardWrapper}
          >
            <div
              style={styles.productCard}
              className="product-card"
            >
              {/* ================================================= */}
              {/* IMAGE */}
              {/* ================================================= */}

              <div
                style={styles.imageWrapper}
                className="product-image-wrapper"
              >
                {/* Product image link */}
                <Link
                  to={`/product/${product.id}`}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <img
                    src={
                      product.img_url ||
                      product.thumbnails?.[0] ||
                      product.variants?.[0]?.mainImage ||
                      product.variants?.[0]?.thumbnails?.[0] ||
                      ""
                    }
                    alt={
                      product.name ||
                      "Product"
                    }
                    style={styles.image}
                    className="product-image"
                    loading="lazy"
                  />
                </Link>

                {/* ================================================= */}
                {/* BADGES */}
                {/* ================================================= */}

                <div
                  style={styles.badgeContainer}
                  className="product-badges"
                >
                  {isOutOfStock ? (
                    <span
                      style={
                        styles.badgeOutOfStock
                      }
                    >
                      OUT OF STOCK
                    </span>
                  ) : (
                    <span
                      style={
                        styles.badgeBestseller
                      }
                    >
                      BESTSELLER
                    </span>
                  )}

                  {Number(
                    product.discount
                  ) > 0 && (
                    <span
                      style={
                        styles.badgeDiscount
                      }
                    >
                      {product.discount}% OFF
                    </span>
                  )}
                </div>

                {/* ================================================= */}
                {/* DESKTOP ACTIONS */}
                {/* ================================================= */}

                <div
                  className="product-actions desktop-actions"
                  style={
                    styles.desktopActionRow
                  }
                >
                  {/* QUICK VIEW */}

                  <button
                    type="button"
                    style={styles.quickView}
                    onClick={(e) =>
                      handleQuickView(
                        product,
                        e
                      )
                    }
                  >
                    Quick View
                  </button>

                  {/* ADD TO BAG */}
                  <ProductCartButton
                    product={product}
                    style={{ borderRadius: "999px" }}
                    onSuccess={(msg) => setMessage(msg)}
                  />
                </div>
              </div>

              {/* ================================================= */}
              {/* PRODUCT INFO */}
              {/* ================================================= */}

              <Link
                to={`/product/${product.id}`}
                style={{
                  textDecoration:
                    "none",
                  color: "inherit",
                  display: "block",
                  flexGrow: 1,
                }}
              >
                <div
                  style={styles.info}
                  className="product-info"
                >
                  <p
                    style={styles.category}
                  >
                    {product.category ||
                      "Handloom Saree"}
                  </p>

                  <h3
                    style={
                      styles.productName
                    }
                    title={product.name}
                  >
                    {product.name}
                  </h3>

                  <div
                    style={styles.priceRow}
                  >
                    <span
                      style={
                        styles.currentPrice
                      }
                    >
                      ₹
                      {Number(
                        product.price || 0
                      ).toLocaleString(
                        "en-IN"
                      )}
                    </span>

                    {product.old_price &&
                      Number(
                        product.old_price
                      ) >
                        Number(
                          product.price
                        ) && (
                        <span
                          style={
                            styles.oldPrice
                          }
                        >
                          ₹
                          {Number(
                            product.old_price
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      )}
                  </div>
                </div>
              </Link>

              {/* ================================================= */}
              {/* MOBILE ACTIONS */}
              {/* ================================================= */}

              <div
                className="product-actions mobile-actions"
                style={
                  styles.mobileActionRow
                }
              >
                {/* QUICK VIEW */}

                <button
                  type="button"
                  style={styles.quickView}
                  onClick={(e) =>
                    handleQuickView(
                      product,
                      e
                    )
                  }
                >
                  Quick View
                </button>

                {/* ADD TO BAG */}
                <ProductCartButton
                  product={product}
                  compact={true}
                  style={{ borderRadius: "999px" }}
                  onSuccess={(msg) => setMessage(msg)}
                />
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* ====================================================== */}
      {/* CSS */}
      {/* ====================================================== */}

      <style>
        {`
          * {
            box-sizing: border-box;
          }

          .product-card {
            transition:
              transform 0.35s cubic-bezier(0.165, 0.84, 0.44, 1),
              box-shadow 0.35s ease,
              border-color 0.35s ease;
            height: 100%;
            display: flex;
            flex-direction: column;
            background: #FFFFFF;
            border-radius: 14px;
            overflow: hidden;
            border: 1px solid rgba(75, 41, 84, 0.1);
            box-shadow: 0 8px 24px rgba(75, 41, 84, 0.06);
          }

          .product-card:hover {
            transform: translateY(-5px);
            border-color: #D4AF37 !important;
            box-shadow:
              0 14px 32px rgba(75, 41, 84, 0.14),
              0 0 16px rgba(212, 175, 55, 0.25) !important;
          }

          .product-image {
            transition:
              transform 0.5s
              cubic-bezier(0.165, 0.84, 0.44, 1);
          }

          .product-card:hover .product-image {
            transform: scale(1.04);
          }

          .view-all-link {
            transition: color 0.2s ease;
          }

          .view-all-link:hover {
            color: #C22730 !important;
          }

          /* =====================================================
             DESKTOP ACTIONS
          ===================================================== */

          @media (min-width: 769px) {
            .product-image-wrapper {
              position: relative;
            }

            .desktop-actions {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              opacity: 0;
              visibility: hidden;
              pointer-events: none;
              transition:
                opacity 0.25s ease,
                visibility 0.25s ease;
              background:
                linear-gradient(
                  to top,
                  rgba(61, 20, 69, 0.92) 0%,
                  rgba(61, 20, 69, 0.45) 70%,
                  transparent 100%
                );
              backdrop-filter: blur(4px);
              padding: 14px;
              display: flex;
              gap: 8px;
              z-index: 10;
            }

            .product-card:hover .desktop-actions {
              opacity: 1;
              visibility: visible;
              pointer-events: auto;
            }

            .mobile-actions {
              display: none !important;
            }
          }

          /* =====================================================
             MOBILE
          ===================================================== */

          @media (max-width: 768px) {
            .bestsellers-container {
              padding: 25px 10px 30px !important;
            }

            .bestsellers-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 10px !important;
              width: 100% !important;
              padding: 0 !important;
            }

            .product-card {
              width: 100% !important;
              border-radius: 12px !important;
              transform: none !important;
            }

            .product-card:hover {
              transform: none !important;
              box-shadow: 0 4px 16px rgba(75, 41, 84, 0.06) !important;
            }

            .product-image-wrapper {
              height: 180px !important;
            }

            .product-info {
              padding: 10px 10px 5px !important;
            }

            .product-category {
              font-size: 8.5px !important;
              letter-spacing: 1px !important;
              color: #927598 !important;
              margin-bottom: 3px !important;
            }

            .product-name {
              font-size: 12px !important;
              margin-bottom: 6px !important;
              color: #3b1443 !important;
            }

            .current-price {
              font-size: 13px !important;
              color: #4B2954 !important;
              font-weight: 700 !important;
            }

            .old-price {
              font-size: 10px !important;
              color: #a48da7 !important;
            }

            /* Hide desktop actions */
            .desktop-actions {
              display: none !important;
            }

            /* Mobile actions */
            .mobile-actions {
              display: flex !important;
              position: relative !important;
              opacity: 1 !important;
              visibility: visible !important;
              pointer-events: auto !important;
              background: transparent !important;
              backdrop-filter: none !important;
              padding: 4px 8px 10px !important;
              gap: 6px !important;
              z-index: 20 !important;
            }

            .mobile-actions button {
              min-height: 33px !important;
              height: 33px !important;
              font-size: 9.5px !important;
              font-weight: 700 !important;
              letter-spacing: 0.3px !important;
              padding: 4px 6px !important;
              border-radius: 999px !important;
              text-transform: uppercase !important;
              touch-action: manipulation !important;
              -webkit-tap-highlight-color: transparent !important;
              cursor: pointer !important;
              transition: transform 0.15s ease !important;
            }

            .mobile-actions button:active {
              transform: scale(0.96) !important;
            }

            .mobile-actions button:first-child {
              background: #fcf8fd !important;
              color: #4B2954 !important;
              border: 1px solid rgba(75, 41, 84, 0.22) !important;
            }

            .mobile-actions button:last-child {
              background: linear-gradient(135deg, #e2bc53 0%, #c99b27 100%) !important;
              color: #2a1236 !important;
              border: none !important;
              box-shadow: 0 2px 8px rgba(201, 155, 39, 0.3) !important;
            }

            .mobile-actions button:disabled,
            .mobile-actions button.out-of-stock,
            .mobile-actions button.vanya-cart-btn-disabled,
            .desktop-actions button:disabled,
            .desktop-actions button.out-of-stock,
            .desktop-actions button.vanya-cart-btn-disabled {
              background: #cbd5e1 !important;
              color: #64748b !important;
              border: none !important;
              box-shadow: none !important;
              cursor: not-allowed !important;
              opacity: 0.9 !important;
            }

            .badgeContainer {
              top: 8px !important;
              left: 8px !important;
            }
          }

          /* =====================================================
             SMALL MOBILE
          ===================================================== */

          @media (max-width: 400px) {
            .bestsellers-container {
              padding-left: 8px !important;
              padding-right: 8px !important;
            }

            .bestsellers-grid {
              gap: 8px !important;
            }

            .product-image-wrapper {
              height: 165px !important;
            }

            .product-info {
              padding: 8px 8px 4px !important;
            }

            .product-name {
              font-size: 11.5px !important;
            }

            .product-category {
              font-size: 8px !important;
            }

            .current-price {
              font-size: 12.5px !important;
            }

            .old-price {
              font-size: 9.5px !important;
            }

            .mobile-actions {
              padding: 3px 6px 8px !important;
              gap: 4px !important;
            }

            .mobile-actions button {
              min-height: 31px !important;
              height: 31px !important;
              font-size: 9px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    padding: "40px 20px 35px",
    maxWidth: "1300px",
    margin: "0 auto",
    fontFamily:
      '"Montserrat", sans-serif',
    backgroundColor: "#FAF5FC",
    boxSizing: "border-box",
    position: "relative",
  },

  cartMessage: {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 9999,
    backgroundColor: "#4B2954",
    color: "#FFFFFF",
    padding: "12px 18px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    boxShadow:
      "0 8px 25px rgba(0,0,0,0.18)",
    maxWidth: "320px",
  },

  header: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "flex-end",
    marginBottom: "35px",
    borderBottom:
      "1px solid rgba(212, 175, 55, 0.25)",
    paddingBottom: "15px",
  },

  tagline: {
    color: "#D4AF37",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "4px",
  },

  title: {
    fontSize:
      "clamp(1.8rem, 3vw, 2.4rem)",
    margin: "6px 0 0",
    fontFamily:
      '"Playfair Display", serif',
    color: "#35133d",
    fontWeight: "700",
  },

  underline: {
    width: "40px",
    height: "2px",
    backgroundColor: "#D4AF37",
    marginTop: "8px",
  },

  viewAll: {
    color: "#4B2954",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: "600",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
  },

  cardWrapper: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  productCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "14px",
    overflow: "hidden",
    border:
      "1px solid rgba(75, 41, 84, 0.1)",
    boxShadow:
      "0 8px 24px rgba(75, 41, 84, 0.06)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
  },

  imageWrapper: {
    position: "relative",
    height: "320px",
    overflow: "hidden",
    backgroundColor: "#F3EBF5",
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  badgeContainer: {
    position: "absolute",
    top: "12px",
    left: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    zIndex: 5,
    pointerEvents: "none",
  },

  badgeOutOfStock: {
    backgroundColor: "#C22730",
    color: "#FFFFFF",
    fontSize: "9px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontWeight: "700",
    letterSpacing: "0.8px",
    boxShadow: "0 2px 8px rgba(194, 39, 48, 0.4)",
  },

  badgeBestseller: {
    backgroundColor: "#3d1445",
    color: "#fbeea4",
    border: "1px solid rgba(226, 188, 83, 0.4)",
    fontSize: "9.5px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontWeight: "700",
    letterSpacing: "1px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },

  badgeDiscount: {
    background: "linear-gradient(135deg, #b31e28 0%, #85121a 100%)",
    color: "#FFFFFF",
    fontSize: "9.5px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    boxShadow: "0 2px 8px rgba(179, 30, 40, 0.3)",
  },

  desktopActionRow: {
    display: "flex",
    gap: "8px",
    width: "100%",
  },

  mobileActionRow: {
    display: "none",
    gap: "8px",
    padding: "0 14px 14px 14px",
    boxSizing: "border-box",
    zIndex: 20,
  },

  quickView: {
    flex: 1,
    padding: "10px 14px",
    border: "none",
    borderRadius: "999px",
    backgroundColor:
      "rgba(255, 255, 255, 0.95)",
    color: "#3d1445",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    touchAction: "manipulation",
    boxShadow:
      "0 4px 12px rgba(0,0,0,0.15)",
    transition: "all 0.2s ease",
  },

  addToCart: {
    flex: 1,
    padding: "10px 14px",
    border: "none",
    borderRadius: "999px",
    background: "linear-gradient(135deg, #fceea7 0%, #e2bc53 50%, #c99b27 100%)",
    color: "#2a1236",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    touchAction: "manipulation",
    boxShadow:
      "0 4px 14px rgba(201, 155, 39, 0.4)",
    transition: "all 0.2s ease",
  },

  info: {
    padding: "16px 14px 10px 14px",
    minWidth: 0,
  },

  category: {
    color: "#8E7394",
    fontSize: "9px",
    margin: "0 0 4px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    fontWeight: "500",
  },

  productName: {
    fontSize: "14px",
    margin: "0 0 8px",
    color: "#4B2954",
    fontFamily:
      '"Playfair Display", serif',
    fontWeight: "600",
    lineHeight: "1.3",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  currentPrice: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#C22730",
  },

  oldPrice: {
    fontSize: "11px",
    color: "#9E8C9E",
    textDecoration: "line-through",
  },
};

export default VanyaBestSellers;
